import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Facebook, Instagram, Send, Check } from "lucide-react";
import { SITE, PHOTOS } from "../data/content";
import Reveal from "../components/Reveal";
import PageHero from "../components/PageHero";
import MapEmbed from "../components/MapEmbed";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INTEREST = ["Cabin Rentals", "Marina / Slips", "Group Event", "General Question"];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: INTEREST[0], message: "", website: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.email.trim()) e.email = "Please enter your email";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Please enter a valid email";
    if (!form.message.trim() || form.message.trim().length < 10) e.message = "Tell us a little more (min 10 chars)";
    setErrors(e);
    return Object.keys(e);
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    const invalid = validate();
    if (invalid.length) {
      document.getElementById(`contact-${invalid[0]}`)?.focus();
      return;
    }
    setStatus("loading");
    try {
      const res = await axios.post(`${API}/contact`, form);
      if (res.data && res.data.ok) {
        setStatus("success");
        toast.success("Thanks! We'll get back to you shortly.");
        setForm({ name: "", email: "", phone: "", interest: INTEREST[0], message: "", website: "" });
      } else {
        throw new Error("Unexpected response");
      }
    } catch (err) {
      setStatus("error");
      toast.error("Something went wrong. Please call us at " + SITE.phoneDisplay);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <main data-testid="contact-page">
      <PageHero
        image={PHOTOS.lakeSunset}
        eyebrow="Contact"
        title="Talk to a real person at the marina."
        subtitle="The best way to reach us is to call. If we don't pick up right away, we'll get back to you shortly."
      />

      <section className="py-24 bg-[var(--ec-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10 grid lg:grid-cols-12 gap-10">
          {/* Contact info */}
          <Reveal className="lg:col-span-5">
            <div className="bg-[var(--ec-navy)] text-white rounded-3xl p-8 lg:p-10 h-full">
              <h2 className="font-display text-3xl sm:text-4xl text-white">Get in touch</h2>
              <p className="mt-4 text-white/80 leading-relaxed">
                Have questions? We'd be glad to answer them. Schedule your visit today — give us a call!
              </p>

              <ul className="mt-10 space-y-5">
                <li className="flex items-start gap-4" data-testid="contact-info-phone">
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Phone className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-white/55">Phone</div>
                    <a href={`tel:${SITE.phoneRaw}`} className="font-display text-2xl text-white">
                      {SITE.phoneDisplay}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-email">
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <Mail className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-white/55">Email</div>
                    <a href={`mailto:${SITE.email}`} className="text-white break-all hover:underline">
                      {SITE.email}
                    </a>
                  </div>
                </li>
                <li className="flex items-start gap-4" data-testid="contact-info-address">
                  <div className="w-11 h-11 rounded-full bg-white/10 flex items-center justify-center shrink-0">
                    <MapPin className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs uppercase tracking-[0.22em] text-white/55">Address</div>
                    <a href={SITE.directions} target="_blank" rel="noopener noreferrer" className="text-white hover:underline">
                      {SITE.address}<span className="sr-only"> (opens map in new tab)</span>
                    </a>
                  </div>
                </li>
              </ul>

              <div className="mt-10 pt-8 border-t border-white/10 flex gap-3">
                <a
                  href={SITE.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Elk Creek Marina on Facebook (opens in new tab)"
                  data-testid="contact-facebook"
                  className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[var(--ec-lake)] transition-colors"
                >
                  <Facebook className="w-6 h-6" />
                </a>
                <a
                  href={SITE.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Elk Creek Marina on Instagram (opens in new tab)"
                  data-testid="contact-instagram"
                  className="w-14 h-14 inline-flex items-center justify-center rounded-full bg-white/10 hover:bg-[var(--ec-lake)] transition-colors"
                >
                  <Instagram className="w-6 h-6" />
                </a>
              </div>
            </div>
          </Reveal>

          {/* Form */}
          <Reveal className="lg:col-span-7" delay={120}>
            <form
              onSubmit={handleSubmit}
              noValidate
              className="bg-white rounded-3xl p-8 lg:p-10 border border-[var(--ec-sand)] shadow-sm"
              data-testid="contact-form"
            >
              <h2 className="font-display text-3xl text-[var(--ec-navy)]">Send us a message</h2>
              <p className="mt-2 text-sm text-[var(--ec-charcoal)]/70">
                Fill out the form and we'll get back to you. Fields marked with * are required.
              </p>

              {/* Honeypot */}
              <input
                aria-label="Leave this field empty"
                type="text"
                name="website"
                value={form.website}
                onChange={onChange("website")}
                tabIndex={-1}
                autoComplete="off"
                className="hidden"
                aria-hidden="true"
              />

              <div className="grid sm:grid-cols-2 gap-5 mt-8">
                <Field label="Name" id="contact-name" required testid="form-name" error={errors.name}>
                  {(a11y) => <input
                    {...a11y}
                    type="text"
                    autoComplete="name"
                    value={form.name}
                    onChange={onChange("name")}
                    className="w-full bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-xl px-4 py-3 focus:border-[var(--ec-lake)] focus:ring-2 focus:ring-[var(--ec-lake)]/20 outline-none transition"
                    placeholder="First and last"
                    data-testid="contact-input-name"
                  />}
                </Field>
                <Field label="Email" id="contact-email" required testid="form-email" error={errors.email}>
                  {(a11y) => <input
                    {...a11y}
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={onChange("email")}
                    className="w-full bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-xl px-4 py-3 focus:border-[var(--ec-lake)] focus:ring-2 focus:ring-[var(--ec-lake)]/20 outline-none transition"
                    placeholder="you@example.com"
                    data-testid="contact-input-email"
                  />}
                </Field>
                <Field label="Phone" id="contact-phone" testid="form-phone">
                  {(a11y) => <input
                    {...a11y}
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={onChange("phone")}
                    className="w-full bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-xl px-4 py-3 focus:border-[var(--ec-lake)] focus:ring-2 focus:ring-[var(--ec-lake)]/20 outline-none transition"
                    placeholder="(optional)"
                    data-testid="contact-input-phone"
                  />}
                </Field>
                <Field label="Interested in" id="contact-interest" testid="form-interest">
                  {(a11y) => <select
                    {...a11y}
                    value={form.interest}
                    onChange={onChange("interest")}
                    className="w-full bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-xl px-4 py-3 focus:border-[var(--ec-lake)] focus:ring-2 focus:ring-[var(--ec-lake)]/20 outline-none transition"
                    data-testid="contact-input-interest"
                  >
                    {INTEREST.map((opt) => (
                      <option key={opt}>{opt}</option>
                    ))}
                  </select>}
                </Field>
              </div>

              <Field label="Message" id="contact-message" required testid="form-message" error={errors.message} className="mt-5">
                {(a11y) => <textarea
                  {...a11y}
                  rows={5}
                  value={form.message}
                  onChange={onChange("message")}
                  className="w-full bg-[var(--ec-cream)] border border-[var(--ec-field-border)] rounded-xl px-4 py-3 focus:border-[var(--ec-lake)] focus:ring-2 focus:ring-[var(--ec-lake)]/20 outline-none transition resize-y"
                  placeholder="Tell us what you have in mind…"
                  data-testid="contact-input-message"
                />}
              </Field>

              <p className="mt-5 text-xs text-[var(--ec-charcoal)]/75 leading-relaxed" data-testid="contact-privacy-notice">
                We only use your details to reply to you. See our{" "}
                <Link to="/privacy" className="underline underline-offset-2 text-[var(--ec-navy)]" data-testid="contact-privacy-link">
                  Privacy Policy
                </Link>
                .
              </p>
              <button
                type="submit"
                disabled={status === "loading"}
                data-testid="contact-submit"
                className="mt-5 inline-flex items-center gap-2 bg-[var(--ec-navy)] text-white hover:bg-[var(--ec-lake)] disabled:opacity-60 font-semibold px-7 py-4 rounded-full btn-lift"
              >
                {status === "success" ? (
                  <>
                    <Check className="w-4 h-4" /> Sent
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" /> {status === "loading" ? "Sending…" : "Send Message"}
                  </>
                )}
              </button>

              <div role="status" aria-live="polite">
                {status === "success" && (
                  <p className="mt-5 text-sm text-[var(--ec-green-deep)] font-medium" data-testid="contact-success">
                    Thanks — your message is in. We'll be in touch soon.
                  </p>
                )}
                {status === "error" && (
                  <p className="mt-5 text-sm text-red-700 font-medium" data-testid="contact-error">
                    Something went wrong sending your message. Please give us a call at {SITE.phoneDisplay}.
                  </p>
                )}
              </div>
            </form>
          </Reveal>
        </div>
      </section>

      {/* Map */}
      <section className="pb-20 bg-[var(--ec-cream)]">
        <div className="max-w-7xl mx-auto px-5 lg:px-10">
          <div className="rounded-3xl overflow-hidden shadow-md border border-[var(--ec-sand)]" data-testid="contact-map">
            <MapEmbed />
          </div>
        </div>
      </section>
    </main>
  );
}

function Field({ label, id, children, error, required = false, className = "", testid }) {
  const errorId = `${id}-error`;
  return (
    <div className={className} data-testid={testid}>
      <label htmlFor={id} className="block text-xs uppercase tracking-[0.18em] font-semibold text-[var(--ec-navy)]/80 mb-2">
        {label}
        {required && <span aria-hidden="true"> *</span>}
      </label>
      {children({
        id,
        required,
        "aria-required": required || undefined,
        "aria-invalid": error ? true : undefined,
        "aria-describedby": error ? errorId : undefined,
      })}
      {error && (
        <div id={errorId} role="alert" className="mt-1.5 text-xs text-red-700 font-medium" data-testid={`${testid}-error`}>
          {error}
        </div>
      )}
    </div>
  );
}
